import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewChecked,
  OnInit,
  OnDestroy
} from '@angular/core';

import { Subject, takeUntil } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { ChatSessionService } from '../../chatbot/services/chat-session.service';
import { ChatUiStateService } from '../../chatbot/services/chat-ui-state.service';
import { ChatMessage, ChatUiState } from '../../chatbot/models/chat.models';
import { ChatMessageComponent } from '../../chatbot/components/chat-message/chat-message.component';
import { ChatTypingIndicatorComponent } from '../../chatbot/components/chat-typing-indicator/chat-typing-indicator.component';
import { ChatInputComponent } from '../../chatbot/components/chat-input/chat-input.component';

/**
 * AssistantChatComponent — área de chat de la página completa del Asistente
 * Virtual. Reutiliza las mismas piezas que el widget flotante (burbuja,
 * indicador de "escribiendo", input) sin su header/FAB/cierre, y agrega
 * reintento de envío tras un error.
 */
@Component({
  selector: 'app-assistant-chat',
  standalone: true,
  imports: [TranslateModule, ChatMessageComponent, ChatTypingIndicatorComponent, ChatInputComponent],
  templateUrl: './assistant-chat.component.html',
  styleUrls: ['./assistant-chat.component.scss']
})
export class AssistantChatComponent implements OnInit, AfterViewChecked, OnDestroy {

  @ViewChild('messagesContainer') private messagesContainer!: ElementRef<HTMLDivElement>;

  messages: ChatMessage[] = [];
  uiState: ChatUiState = { isOpen: true, isLoading: false, unreadCount: 0, error: null };

  private lastSentText: string | null = null;
  private shouldScrollToBottom = false;
  private wasOpenBeforeMount = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private session: ChatSessionService,
    private uiStateService: ChatUiStateService
  ) {}

  ngOnInit(): void {
    // Mientras la página completa está montada, se marca el chat como
    // "abierto" para que ChatUiStateService.incrementUnread() (usado por el
    // badge del widget flotante) nunca sume acá — de lo contrario cada
    // respuesta leída en esta página infla un badge que nadie ve. isOpen es
    // un flag global compartido con el widget flotante, así que se guarda su
    // valor previo para restaurarlo al salir (ver ngOnDestroy) y no forzar
    // cerrado un widget que el usuario ya tenía abierto en otra ruta.
    this.wasOpenBeforeMount = this.uiStateService.snapshot.isOpen;
    this.uiStateService.open();

    this.session.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(msgs => {
        this.messages = msgs;
        this.shouldScrollToBottom = true;
      });

    this.uiStateService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.uiState = state;
        if (state.isLoading) {
          this.shouldScrollToBottom = true;
        }
      });
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Restaura el estado previo del widget flotante en vez de forzarlo
    // cerrado incondicionalmente (evita clobbearlo si ya estaba abierto).
    if (this.wasOpenBeforeMount) {
      this.uiStateService.open();
    } else {
      this.uiStateService.close();
    }
  }

  onSend(text: string): void {
    this.lastSentText = text;
    this.session.sendMessage(text);
  }

  retry(): void {
    if (!this.lastSentText) return;
    this.session.sendMessage(this.lastSentText);
  }

  dismissError(): void {
    this.uiStateService.setError(null);
  }

  trackByMessageId(_index: number, msg: ChatMessage): string {
    return msg.id;
  }

  private scrollToBottom(): void {
    try {
      const el = this.messagesContainer?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    } catch { /* noop */ }
  }
}
